import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FormFieldContext } from '../FormField/FormFieldContext';
import uuidv4 from '../../utils/uuidv4';
/**
 * Bronson Textarea component.
 * Essentially, a `<textarea>` element uses the same markup as input.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-textarea.html
 * @constructor
 */
export function Textarea({ charCounter, children, counterHint, counterMessageFormatter, counterMessageFormatterDebounce = 500, enableMaxLength = true, error, id, maxCounter, modifierStates, onChange, success, testId, ...otherProps }) {
    /**
     * Generate unique id, if prop `id` is not provided to improve accessibility.
     */
    const textareaId = useMemo(() => id ?? uuidv4('textarea-'), [id]);
    /**
     * Track the current character count.
     */
    const [currentCount, setCurrentCount] = useState(0);
    /**
     * Track the textarea hint message state.
     */
    const [hintMessage, setHintMessage] = useState(undefined);
    /**
     * Track the internal state via {@link FormFieldContext}.
     * @type {object}
     */
    const context = useContext(FormFieldContext);
    /**
     * Debounce the hint message for assistive technologies.
     */
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const message = maxCounter
                ? (counterMessageFormatter?.(maxCounter - currentCount) ??
                    `You have ${maxCounter - currentCount} characters remaining.`)
                : undefined;
            setHintMessage(message);
        }, counterMessageFormatterDebounce);
        return () => clearTimeout(timeoutId);
    }, [currentCount, maxCounter, counterMessageFormatterDebounce]);
    /**
     * 1. Update the context state and mark the component as active.
     * 2. Set the {@link currentCount} based on the character length of the `<textarea>`.
     */
    useEffect(() => {
        if (context?.setElementActive) {
            context.setElementActive(!!children);
        }
        setCurrentCount(children?.length ?? 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children]);
    const wrapperClassNameList = classNames('c-input', 'c-input--textarea', {
        'is-success': success,
    }).trim();
    const textareaClassNameList = classNames('c-input__input', {
        'is-success': success,
        [`${modifierStates} `]: modifierStates,
    }).trim();
    const counterClassNameList = classNames('c-input__counter', {
        'c-input__counter--alert': typeof maxCounter === 'number' ? currentCount >= maxCounter : false,
    }).trim();
    /**
     * Conditionally render an `[aria-describedby]` attribute,
     * Mixin any global passed `aria-describedby` into the char hint attribute.
     */
    const describedByAttr = [
        charCounter && maxCounter ? `${textareaId}-counter-hint-textarea-counter-error` : undefined,
        otherProps?.['aria-describedby'] ?? undefined,
    ]
        .filter((label) => !!label)
        .join(' ')
        .trim() || undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: wrapperClassNameList },
            React.createElement("textarea", { defaultValue: children, id: textareaId, className: textareaClassNameList, "aria-invalid": error ? true : undefined, "data-testid": testId, onChange: (event) => {
                    charCounter && setCurrentCount(event.target.value.length);
                    return [onChange?.(event), context.setElementActive && context.setElementActive(!!event.target.value)];
                }, ...(enableMaxLength && maxCounter ? { maxLength: maxCounter } : {}), ...otherProps, "aria-describedby": describedByAttr })),
        charCounter && maxCounter && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: counterClassNameList, "aria-hidden": "true" },
                currentCount,
                " / ",
                maxCounter),
            React.createElement("div", { className: "u-visually-hidden" },
                React.createElement("div", { id: `${textareaId}-counter-hint-textarea-counter-error` }, counterHint ?? `You can enter up to ${maxCounter} characters.`),
                React.createElement("div", { className: "c-input__counter", id: `${textareaId}-counter`, "aria-live": "polite" }, hintMessage ??
                    counterMessageFormatter?.(maxCounter - currentCount) ??
                    `You have ${maxCounter - currentCount} characters remaining.`))))));
}
